import { Navbar } from '@/components/molecules/Navbar/Navbar'
import {
  Box,
  Text,
  Flex,
  Link as ChakraLink,
  Grid,
  useToken,
  Icon,
  Container,
  useMediaQuery,
  FlexProps,
  TextProps,
  Button,
  useDisclosure,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  SimpleGrid,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { BiLogoDiscordAlt, BiLogoMedium, BiLogoTelegram, BiSupport } from 'react-icons/bi'
import { FaXTwitter } from 'react-icons/fa6'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { usePathname, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { Image } from '@/components/HOC/Image'

import MinesIcon from '@/public/assets/images/games-icons/mines-icon-96x96.png'
import JackpotIcon from '@/public/assets/images/games-icons/jackpot-icon-96x96.png'
import DoubleIcon from '@/public/assets/images/games-icons/double-icon-96x96.png'
import LootboxIcon from '@/public/assets/images/games-icons/lootbox-icon-96x96.png'
import CoinflipIcon from '@/public/assets/images/games-icons/coinflip-icon-96x96.png'
import RunesIcon from '@/public/assets/images/games-icons/runes-icon-96x96.png'
import PlinkoIcon from '@/public/assets/images/games-icons/plinko-icon-96x96.png'
import GameZoneIcon from '@/public/assets/images/games-icons/game-zone-icon-96x96.png'

import React, { useState, useEffect } from 'react'
import ChatIcon from '@/public/assets/icons/chat.svg'
import { useLocalStorage, useSessionStorage } from 'usehooks-ts'
import { LocalStorageKey } from '@/constants/localstorage'
import { Logo } from '../atoms/Logo/Logo'
import { useEnvironment } from '@/hooks/useEnvironment'
import { useGeoLocation } from '@/hooks/useGeoLocation'
import { Chat } from '../organisms/Chat/Chat'
import { useReferrer } from '@/hooks/useReferral'
import { useMaintenance } from '@/hooks/useMaintenance'
import WalletIcon from '@/public/assets/icons/wallet.svg'
import UserIcon from '@/public/assets/icons/user.svg'
import MenuIcon from '@/public/assets/icons/menu.svg'
import GamepadIcon from '@/public/assets/icons/game-controller-solid.svg'
import { MobileMenu as MobileSidebar } from '@/components/molecules/MobileMenu/MobileMenu'
import DatabaseIcon from '@/public/assets/icons/database.svg'
import UsersGroupIcon from '@/public/assets/icons/users-group.svg'
import RefreshCircleIcon from '@/public/assets/icons/refresh-circle.svg'
import { colors } from '@/theme'

export const Layout = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => {
  const { saveReferrer } = useReferrer()
  const searchParams = useSearchParams()
  const { isProduction } = useEnvironment()
  const modal = useAppModal()
  const { restricted } = useGeoLocation()
  const [countryRestrictedModalIgnore] = useSessionStorage(LocalStorageKey.CountryRestrictionIgnore, false)

  useEffect(() => {
    modal.openModalNew(AppModals.CountryRestrictedModal)
    if (restricted && isProduction && !countryRestrictedModalIgnore) {
      modal.openModalNew(AppModals.CountryRestrictedModal)
    }
  }, [restricted, isProduction])

  useEffect(() => {
    let referrer = searchParams.get('r')
    const campaign = searchParams.get('c')

    if (referrer || campaign) {
      referrer = referrer || '0x850e37488072fde3a3b076f59a06e3f89ab0f713'
      saveReferrer(referrer || '', campaign)
    }
  }, [searchParams])

  return (
    <>
      <Sidebar games={games} />
      <Box ml={{ base: 0, sm: '92px', lg: 56 }} bg={'ninja.1000'} whiteSpace={'pre-wrap'}>
        <MaintenanceInfo />
        <Navbar />
        <MobileMenu />
        <GridLayout>{children}</GridLayout>
        <Footer />
      </Box>
    </>
  )
}

const GridLayout = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => {
  const [isChatOpen, setChatOpen] = useState(false)
  const [isRendered, setRendered] = useState(false)
  const { isProduction } = useEnvironment()
  const [isDesktop] = useMediaQuery('(min-width: 1524px)')
  const [chatMode, setChatMode] = useLocalStorage(LocalStorageKey.Chat, true)
  const isChatDisplaying = isChatOpen && isRendered && isProduction

  const handleChatMode = (mode: boolean) => {
    setChatOpen(mode)
    setChatMode(mode)
  }

  useEffect(() => {
    if (isDesktop && chatMode) {
      setChatOpen(true)
    }
  }, [isDesktop])

  useEffect(() => {
    setRendered(true)
  }, [])

  return (
    <Grid
      display={{ base: 'block', '2xl': isChatDisplaying ? 'grid' : 'block' }}
      gridTemplateColumns={{ base: '1fr', '2xl': '1fr 300px' }}
    >
      <Box w={'100%'} minH={'100vh'} mx={'auto'} py={{ base: 6, md: 10 }} pb={{ base: 16, md: 24 }} gap={8}>
        <Container maxW={'7xl'} px={6} className="page-transition">
          <Transition>{children}</Transition>
        </Container>
      </Box>
      {isChatDisplaying && (
        <Box
          pos={{ base: 'fixed', '2xl': 'sticky' }}
          zIndex={50}
          bg={{ base: 'ninja.600', '2xl': 'transparent' }}
          mt={{ base: 0, '2xl': 6 }}
          top={{ base: 0, '2xl': 6 }}
          roundedLeft={'xl'}
          bottom={{ base: 0, '2xl': 'unset' }}
          right={{ base: 0, '2xl': 'unset' }}
          h={{ base: 'auto', '2xl': 'calc(100vh - 112px)' }}
        >
          <Chat onClose={() => handleChatMode(false)} />
        </Box>
      )}
      {!isChatOpen && isRendered && isProduction && (
        <>
          <Box
            onClick={() => handleChatMode(true)}
            pos={'fixed'}
            w={12}
            right={0}
            p={3}
            zIndex={20}
            cursor={'pointer'}
            roundedLeft={'xl'}
            bottom={200}
            bg={'ninja.500'}
          >
            <ChatIcon />
          </Box>
        </>
      )}
    </Grid>
  )
}

const MaintenanceInfo = () => {
  const { maintenanceMessage } = useMaintenance()

  if (!maintenanceMessage) {
    return null
  }

  return (
    <Box
      pos={'relative'}
      zIndex={30}
      bg={'ninja.400'}
      color={'ninja.100'}
      fontWeight={700}
      fontSize={'xs'}
      py={1}
      px={4}
      textAlign={'center'}
    >
      {maintenanceMessage}
    </Box>
  )
}

const MobileMenu = () => {
  const { t } = useTranslation()
  const { account, signIn } = useSession()
  const { asPath, push, events } = useRouter()
  const modal = useAppModal()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure({ defaultIsOpen: false })

  useEffect(() => {
    events.on('routeChangeStart', onMenuClose)
    return () => {
      events.off('routeChangeStart', onMenuClose)
    }
  }, [events])

  return (
    <>
      <Box
        display={{ base: 'block', sm: 'none' }}
        pos={'fixed'}
        bottom={2.5}
        zIndex={30}
        left={'50%'}
        transform={'translateX(-50%)'}
        w={'full'}
        maxW={'368px'}
      >
        <Box pos={'relative'}>
          <Flex
            w={'full'}
            rounded={'20px'}
            bg={'rgba(18, 18, 54, 0.60)'}
            backdropFilter={'blur(40px)'}
            p={2}
            gap={2}
            color={'ninja.200'}
            fontSize={'10px'}
            fontWeight={600}
            justifyContent={'space-between'}
          >
            <Flex flex={1} gap={2}>
              <Link href="/affiliate">
                <MobileMenuElement text={t('navigation.affiliate')} icon={PaperIcon} />
              </Link>
              {account && (
                <Box onClick={() => modal.openModalNew(AppModals.DepositModal2)}>
                  <MobileMenuElement text={t('navigation.wallet')} icon={WalletIcon} />
                </Box>
              )}
            </Flex>
            <Popover placement="top" closeOnBlur>
              <PopoverScrollCloseTrigger />
              <PopoverTrigger>
                <Button
                  fontSize={'10px'}
                  gap={1}
                  p={0}
                  mt={'-12px'}
                  w={'78px'}
                  h={'78px'}
                  rounded={20}
                  flexDir={'column'}
                >
                  <Icon as={GamepadIcon} height={'36px'} width={'36px'} />
                  <Text>{t('navigation.games')}</Text>
                </Button>
              </PopoverTrigger>
              <PopoverContent border={'none'} bg={'transparent'} w={'auto'}>
                <PopoverBody p={0}>
                  <Grid
                    p={2}
                    gap={2}
                    templateColumns="repeat(3, 1fr)"
                    rounded="16px"
                    bg="ninja.700"
                    boxShadow="0px 12px 40px 0px rgba(23, 23, 65, 0.40)"
                  >
                    {mobileGames.map((game, index) => {
                      const isGameZone = game.pathname === '/game-zone'

                      return (
                        <Link
                          key={index}
                          href={game.pathname || ''}
                          style={{ display: 'block', gridRow: isGameZone ? 'span 2' : undefined }}
                        >
                          <Flex
                            h={isGameZone ? '136px' : undefined}
                            minH={16}
                            w={28}
                            bg="ninja.600"
                            rounded="16px"
                            justifyContent="center"
                            alignItems="center"
                            gap={1}
                            flexDir="column"
                            pos="relative"
                          >
                            <Box boxSize={8}>{game.image}</Box>
                            <Text fontSize="10px" fontWeight={600} color="ninja.100" textAlign={'center'}>
                              {t(game.name)}
                            </Text>
                          </Flex>
                        </Link>
                      )
                    })}
                  </Grid>
                </PopoverBody>
              </PopoverContent>
            </Popover>
            <Flex flex={1} gap={2} justifyContent={'flex-end'}>
              {account ? (
                <Box
                  onClick={() => {
                    push('/activity/' + account.injectiveAddress)
                  }}
                >
                  <MobileMenuElement
                    isActive={asPath.includes('/activity/' + account?.injectiveAddress)}
                    text={t('navigation.account')}
                    icon={UserIcon}
                  />
                </Box>
              ) : (
                <Box onClick={() => signIn()}>
                  <MobileMenuElement text={t('navigation.account')} icon={UserIcon} />
                </Box>
              )}
              <Box
                onClick={() => {
                  onMenuOpen()
                }}
              >
                <MobileMenuElement text={t('navigation.menu')} icon={MenuIcon} />
              </Box>
            </Flex>
          </Flex>
        </Box>
      </Box>
      <MobileSidebar isOpen={isMenuOpen} onClose={onMenuClose} />
    </>
  )
}

const MobileMenuElement = ({ text, icon, isActive = false }: { text: string; icon: any; isActive?: boolean }) => {
  return (
    <Flex
      w={'60px'}
      h={16}
      flexDir={'column'}
      gap={1}
      justifyContent={'center'}
      alignItems={'center'}
      transition={'0.1'}
      color={isActive ? 'ninja.100' : 'inherit'}
      _hover={{
        color: 'ninja.100',
      }}
    >
      <Icon as={icon} height={'24px'} width={'24px'} />
      <Text>{text}</Text>
    </Flex>
  )
}

const Sidebar = ({ games }: { games: Array<GAME_ITEM> }) => {
  const [scrollbarColor] = useToken('colors', ['ninja.900'])

  return (
    <Flex
      display={{ base: 'none', sm: 'flex' }}
      flexDirection={'column'}
      justifyContent={'space-between'}
      position={'fixed'}
      top={0}
      left={0}
      bottom={0}
      zIndex={20}
      w={{ sm: '92px', lg: 56 }}
      color={'white'}
      bg={'ninja.700'}
      overflowY={'auto'}
      px={4}
      pb={6}
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarColor,
          borderRadius: '24px',
        },
      }}
    >
      <Flex
        pb={7}
        pt={6}
        gap={3}
        alignItems={'center'}
        justifyContent={'center'}
        position={'sticky'}
        top={0}
        bg={'ninja.700'}
        zIndex={10}
      >
        <Logo textStyles={{ fontSize: '22px', display: { sm: 'none', lg: 'block' } }} />
      </Flex>
      <Flex flexDir={'column'} justifyContent={'space-between'} flexGrow={1}>
        <Box>
          <GamesList games={games} />
          {/* <LaunchpadWidget /> */}
          <RelevantLinks />
        </Box>
        <Flex flexDir={'column'} gap={{ sm: 6, lg: 8 }}>
          <NetworkStatus />
          <SocialMediaLinks />
        </Flex>
      </Flex>
    </Flex>
  )
}

const LaunchpadCounter = () => {
  const { t } = useTranslation()
  return (
    <Box>
      <Text lineHeight={1} color={'ninja.100'} fontWeight={700} fontSize={'xs'} mb={1}>
        {t('navigation.auction-basket')}
      </Text>

      <Flex alignItems={'center'} gap={1}>
        <Pulsar />
        <Text lineHeight={1} minW={'90px'} fontWeight={700} suppressHydrationWarning>
          {t('common.live')}
        </Text>
      </Flex>
    </Box>
  )
}

const Footer = () => {
  return (
    <Box as={'footer'} mb={{ base: '90px', sm: 0 }}>
      <Container maxW={'7xl'} px={6}>
        <Flex
          py={10}
          borderTop={'1px solid'}
          borderColor={'ninja.500'}
          justifyContent={'space-between'}
          alignItems={{ base: 'center', sm: 'center', md: 'start', xl: 'center' }}
          flexDir={{ base: 'column-reverse', md: 'row' }}
        >
          <Box mt={{ base: 6, md: 2, xl: 0 }}>
            <Flex justifyContent={{ base: 'center', md: 'start' }} mb={3}>
              <Logo />
            </Flex>
            <AdditionalLinks />
          </Box>
          <FooterStats />
        </Flex>
      </Container>
    </Box>
  )
}

const FooterStats = () => {
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: ['PLATFORM_STATS'],

    queryFn: () =>
      fetch(API_GATEWAY + '/stats/now')
        .then((res) => res.json())
        .then((res) => ({
          volume: res.totalVolume,
          games: res.totalGames,
          users: res.totalUsers,
        })),

    refetchInterval: 5 * 60 * 1_000,
  })

  return (
    <Box
      display={{ base: 'grid', xl: 'flex' }}
      gridTemplateColumns={'1fr 1fr 1fr'}
      gap={{ base: 6, lg: 10 }}
      rounded={'20px'}
      border={'1px solid'}
      borderColor={'ninja.500'}
      py={6}
      px={{ base: 3, xl: 8 }}
    >
      <FooterStatsElement
        number={
          <Text minW={20} suppressHydrationWarning>
            {!!data ? Number(data.volume).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '2,000,000'}
          </Text>
        }
        text={
          <Text fontSize={'xs'} color={'ninja.200'}>
            {t('stats.usdc-volume')}
          </Text>
        }
        icon={<DatabaseIcon />}
      />
      <FooterStatsElement
        number={!!data ? Number(data.games).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '330,000'}
        text={
          <Text fontSize={'xs'} color={'ninja.200'}>
            {t('stats.games')}
          </Text>
        }
        icon={<RefreshCircleIcon />}
      />
      <FooterStatsElement
        number={!!data ? Number(data.users).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '19,100'}
        text={
          <Text fontSize={'xs'} color={'ninja.200'}>
            {t('stats.players')}
          </Text>
        }
        icon={<UsersGroupIcon />}
      />
    </Box>
  )
}

const FooterStatsElement = ({
  number,
  text,
  icon,
}: {
  number: React.ReactElement | string
  text: React.ReactElement
  icon: any
}) => {
  return (
    <Flex gap={3} alignItems={'center'} flexDir={{ base: 'column', xl: 'row' }}>
      <StatIcon icon={icon} />
      <Box fontWeight={700} textAlign={{ base: 'center', xl: 'start' }}>
        {number}
        {text}
      </Box>
    </Flex>
  )
}

const StatIcon = ({ icon }: { icon: any }) => {
  return (
    <Flex
      boxSize={10}
      bgGradient={colors.gradient.primary}
      rounded={'full'}
      boxShadow={'0px 6px 15px 0px rgba(143, 132, 252, 0.44);'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      {icon}
    </Flex>
  )
}

import NFTIcon from '@/public/assets/images/common/nfts-icon.png'
import Arrowcon from '@/public/assets/icons/arrow-simple.svg'

export const NftWidget = () => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['NFT_PRICE'],
    queryFn: async () => await fetch('https://app-mainnet.blaze.ninja/api/stats/nfts').then((res) => res.json()),
  })

  const isLoaded = !isLoading && !isError

  return (
    <Flex
      mt={6}
      mb={6}
      p={{ base: 3, sm: 2.5, lg: 3 }}
      justifyContent={'space-between'}
      alignItems={'center'}
      border={'1px solid'}
      rounded={'2xl'}
      borderColor={'whiteAlpha.100'}
      cursor={'pointer'}
      _hover={{
        bg: 'ninja.600',
      }}
      transition={'0.1s'}
    >
      <a href="https://injective.talis.art/collection/65d214819b0824cc8e79d0d9" target="_blank">
        <Flex alignItems={'center'} gap={2}>
          <Flex boxSize={9} justifyContent={'center'} alignItems={'center'}>
            <Image w={'75px'} maxW={'none'} alt={''} src={NFTIcon} />
          </Flex>
          <Box display={{ base: 'block', sm: 'none', lg: 'block' }}>
            <Text fontWeight={700} fontSize={'sm'}>
              {t('navigation.nft-collection')}
            </Text>
            {isLoaded ? (
              <Amount denom={INJ_DENOM} value={Number(data.floorPrice).toFixed(2)} />
            ) : (
              <Skeleton startColor={'ninja.400'} endColor={'ninja.500'} h={'17px'} w={'70px'} rounded={'md'} />
            )}
          </Box>
        </Flex>
      </a>
      <Icon display={{ base: 'block', sm: 'none', lg: 'block' }} w={'6px'} height={'10px'} as={Arrowcon} />
    </Flex>
  )
}

import TokenGif from '@/public/assets/images/home/token-gif.gif'

export const LaunchpadWidget = () => {
  return (
    <Link href="/auction">
      <Flex
        sx={{
          '@keyframes pulsateBorder': {
            '0%': {
              borderColor: 'whiteAlpha.100',
            },
            '100%': {
              borderColor: '#8E83FB50',
            },
          },
        }}
        mt={6}
        mb={6}
        p={{ base: 3, sm: 2.5, lg: 3 }}
        px={{ base: 3, sm: 2.5, lg: 2 }}
        justifyContent={'space-between'}
        alignItems={'center'}
        border={'1px solid'}
        rounded={'2xl'}
        borderColor={'whiteAlpha.100'}
        cursor={'pointer'}
        _hover={{
          bg: 'ninja.600',
        }}
        transition={'0.2s'}
        animation={'pulsateBorder 0.5s infinite alternate'}
      >
        <Flex gap={1.5} alignItems={'center'}>
          <Image w={10} alt={''} src={TokenGif} quality={100} />
          <Box display={{ base: 'block', sm: 'none', lg: 'block' }}>
            <LaunchpadCounter />
          </Box>
        </Flex>
        <Icon display={{ base: 'block', sm: 'none', lg: 'block' }} w={'6px'} mr={1} height={'10px'} as={Arrowcon} />
      </Flex>
    </Link>
  )
}

export const GamesList = ({ games }: { games: Array<GAME_ITEM> }) => {
  const pathname = usePathname()

  return (
    <SimpleGrid gap={2} mb={2} columns={{ base: 3, sm: 1 }}>
      {games.map(({ pathname: gamePathname, name, image, isHighlighted = false, isFree = false }, index) => {
        return (
          <Link key={index} href={gamePathname || ''}>
            <GamePanel
              isHighlighted={isHighlighted}
              isFree={isFree}
              title={name}
              icon={image}
              isActive={gamePathname ? pathname?.startsWith(gamePathname) : false}
              textWrapperStyles={{ display: { base: 'flex', sm: 'none', lg: 'flex' } }}
            />
          </Link>
        )
      })}
    </SimpleGrid>
  )
}

export const AdditionalLinks = () => {
  const { t } = useTranslation()

  return (
    <Flex
      flexWrap={'wrap'}
      justifyContent={{ base: 'center', sm: 'start' }}
      color={'ninja.200'}
      fontSize={'xs'}
      columnGap={4}
      rowGap={1}
    >
      <Box
        _hover={{
          color: 'ninja.100',
        }}
        transition={'0.1s'}
      >
        <Link target="_blank" href={'https://docs.blaze.ninja/additional/terms-and-conditions'}>
          <Text as={'span'}>{t('common.terms-and-conditions')}</Text>
        </Link>
      </Box>
      <Box
        _hover={{
          color: 'ninja.100',
        }}
        transition={'0.1s'}
      >
        <Link target="_blank" href={'https://docs.blaze.ninja/introduce/links-and-contact-us'}>
          <Text as={'span'}>{t('common.contact')}</Text>
        </Link>
      </Box>
      <Box
        _hover={{
          color: 'ninja.100',
        }}
        transition={'0.1s'}
      >
        <Link target="_blank" href={'https://docs.blaze.ninja/'}>
          <Text as={'span'}>{t('navigation.whitepaper')}</Text>
        </Link>
      </Box>
      <Box
        _hover={{
          color: 'ninja.100',
        }}
        transition={'0.1s'}
      >
        <Link href={'/verify'}>
          <Text as={'span'}>{t('navigation.provably-fair')}</Text>
        </Link>
      </Box>
    </Flex>
  )
}

const SocialMediaElement = ({ link, icon }: { link: string; icon: () => React.ReactElement }) => {
  return (
    <ChakraLink
      color={'ninja.200'}
      padding={{ base: 2.5, lg: 3 }}
      rounded={{ base: 'xl', lg: 'lg' }}
      bg={'ninja.600'}
      _hover={{
        color: 'ninja.100',
        bg: 'ninja.500',
      }}
      target="_blank"
      href={link}
    >
      {icon()}
    </ChakraLink>
  )
}

const SocialMedia = [
  {
    link: 'https://t.me/NinjaBlazeApp',
    icon: () => <BiLogoTelegram size={20} />,
  },
  {
    link: 'https://x.com/NinjaBlazeApp',
    icon: () => <FaXTwitter size={20} />,
  },
  {
    link: 'https://discord.gg/X7h9nRMUBR',
    icon: () => <BiLogoDiscordAlt size={20} />,
  },
  {
    link: 'https://medium.com/ninja-blaze',
    icon: () => <BiLogoMedium size={20} />,
  },
]

import SocialMediaIcon from '@/public/assets/icons/social-media.svg'

export const SocialMediaLinks = () => {
  return (
    <Box pos={'relative'}>
      <Flex
        display={{ base: 'flex', sm: 'none', lg: 'flex' }}
        pos={{ sm: 'absolute', lg: 'static' }}
        left={'100%'}
        top={0}
        justifyContent={{ base: 'center', sm: 'space-between' }}
        fontSize={24}
        gap={{ base: 1.5, sm: 0 }}
      >
        {SocialMedia.map((item, index) => (
          <SocialMediaElement key={index} {...item} />
        ))}
      </Flex>
      <Popover strategy={'fixed'} placement={'right'}>
        <PopoverTrigger>
          <Button
            variant={'unstyled'}
            mx={'auto'}
            w={'44px'}
            h={'44px'}
            justifyContent={'center'}
            alignItems={'center'}
            rounded={'lg'}
            bg={'ninja.600'}
            display={{ base: 'none', sm: 'flex', lg: 'none' }}
            cursor={'pointer'}
            color={'ninja.200'}
            _hover={{
              bg: 'ninja.500',
              color: 'ninja.100',
            }}
          >
            <Icon boxSize={5} as={SocialMediaIcon} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          bg={'ninja.700'}
          border={'none'}
          w={'min-content'}
          rounded={'2xl'}
          boxShadow={'0px 4px 32px 0px rgba(0, 0, 0, 0.24);'}
        >
          <PopoverBody p={2}>
            <Flex justifyContent={'space-between'} gap={1} fontSize={24}>
              {SocialMedia.map((item, index) => (
                <SocialMediaElement key={index} {...item} />
              ))}
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  )
}

export const GamePanel = ({
  isActive = false,
  icon,
  title,
  isHighlighted = false,
  isFree = false,
  wrapperStyles,
  textStyles,
  textWrapperStyles,
}: {
  isActive?: boolean
  isHighlighted?: boolean
  isFree?: boolean
  icon: React.ReactElement | null
  title: string
  wrapperStyles?: FlexProps
  textStyles?: TextProps
  textWrapperStyles?: FlexProps
}) => {
  const { t } = useTranslation()

  return (
    <Box>
      <Flex
        bg={isActive ? 'ninja.500' : 'ninja.600'}
        _hover={{
          bg: 'ninja.500',
          color: 'ninja.100',
        }}
        cursor={'pointer'}
        py={{ base: 4, sm: 1.5 }}
        px={2}
        rounded={{ base: '2xl', sm: 'lg' }}
        alignItems={'center'}
        flexDir={{ base: 'column', sm: 'row' }}
        justifyContent={{ sm: 'center', lg: 'start' }}
        gap={2.5}
        pos={'relative'}
        color={isActive ? 'ninja.100' : 'ninja.200'}
        {...wrapperStyles}
      >
        <Box boxSize={{ base: 12, sm: 10 }}>{icon}</Box>
        <Flex flexDirection={'column'} alignItems={'start'} gap={1} {...textWrapperStyles}>
          <Text fontSize={{ base: 'xs', sm: 'sm' }} fontWeight={500} lineHeight={'16px'} {...textStyles}>
            {t(title)}
          </Text>
          {(isFree || isHighlighted) && (
            <Box
              bg={isHighlighted ? 'brand.yellow' : 'ninja.400'}
              pos={'absolute'}
              color={isHighlighted ? 'ninja.600' : 'white'}
              fontSize={'10'}
              fontWeight={700}
              top={1.5}
              right={1.5}
              px={{ base: 0.5, sm: 1.5 }}
              py={0.5}
              rounded={'full'}
            >
              {isHighlighted ? t('common.new') : 'FREE'}
            </Box>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}

import PaperIcon from '@/public/assets/icons/paper.svg'
import { NetworkStatus } from '../atoms/NetworkStatus/NetworkStatus'
import { Amount } from '../atoms/Amount/Amount'
import { useRouter } from 'next/router'
import { useSession } from '@/context/SessionProvider'
import { useQuery } from '@tanstack/react-query'
import { PopoverScrollCloseTrigger } from '../HOC/PopoverScrollCloseTrigger'
import { API_GATEWAY, INJ_DENOM } from '@/constants/constants'
import { AnimatePresence, motion } from 'framer-motion'
import { Pulsar } from '../atoms/Pulsar/Pulsar'

export const RelevantLinks = ({ onClose }: { onClose?: () => void }) => {
  const { t } = useTranslation()
  const pathname = usePathname()

  return (
    <>
      <Flex flexDir={'column'} alignItems={{ sm: 'center', lg: 'stretch' }} gap={2} mb={6} mt={6}>
        <Link href="/affiliate" onClick={onClose}>
          <RelevantLinksElement
            icon={<Icon as={PaperIcon} height={'20px'} width={'20px'} />}
            text={t('navigation.affiliate-program')}
            isActive={pathname === '/affiliate'}
          />
        </Link>
        <Box
          onClick={() => {
            window.$chatwoot.toggle('open')
          }}
        >
          <RelevantLinksElement
            icon={<Icon as={BiSupport} height={'20px'} width={'20px'} />}
            text={t('navigation.support')}
            isActive={false}
          />
        </Box>
      </Flex>
    </>
  )
}

const RelevantLinksElement = ({ icon, text, isActive }: { icon: any; text: string; isActive: boolean }) => {
  return (
    <Flex
      gap={3}
      px={3}
      py={2.5}
      cursor={'pointer'}
      transition={'.1s'}
      _hover={{
        bg: 'ninja.500',
        color: 'ninja.100',
      }}
      _focus={{
        bg: 'ninja.500',
      }}
      alignItems={'center'}
      bg={isActive ? 'ninja.500' : 'ninja.600'}
      color={isActive ? 'ninja.100' : 'ninja.200'}
      rounded={'xl'}
    >
      {icon}
      <Text display={{ base: 'block', sm: 'none', lg: 'block' }} fontSize={'sm'} fontWeight={500}>
        {text}
      </Text>
    </Flex>
  )
}

export type GAME_ITEM = {
  name: string
  pathname: string | null
  image: React.ReactElement | null
  isActive: boolean
  isHighlighted?: boolean
  isFree?: boolean

  mobileMenuOrder?: number
}

export const games: Array<GAME_ITEM> = [
  {
    name: 'games.plinko',
    pathname: '/plinko',
    image: <Image src={PlinkoIcon} alt={''} />,
    isActive: true,
    isHighlighted: true,
    mobileMenuOrder: 1,
  },
  {
    name: 'games.runes',
    pathname: '/runes',
    image: <Image src={RunesIcon} alt={''} />,
    isActive: false,
    isHighlighted: false,
    mobileMenuOrder: 2,
  },
  {
    name: 'games.double',
    pathname: '/double',
    image: <Image src={DoubleIcon} alt={''} />,
    isActive: true,
    mobileMenuOrder: 3,
  },
  {
    name: 'games.coinflip',
    pathname: '/coinflip',
    image: <Image src={CoinflipIcon} alt={''} />,
    isActive: true,
    isHighlighted: false,
    mobileMenuOrder: 4,
  },
  {
    name: 'games.mines',
    pathname: '/mines',
    image: <Image src={MinesIcon} alt={''} />,
    isActive: true,
    mobileMenuOrder: 5,
  },
  {
    name: 'games.lootbox',
    pathname: '/lootbox',
    image: <Image src={LootboxIcon} alt={''} />,
    isActive: true,
    mobileMenuOrder: 7,
  },
  {
    name: 'games.jackpot',
    pathname: '/jackpot',
    image: <Image src={JackpotIcon} alt={''} />,
    isActive: true,
    isFree: true,
    mobileMenuOrder: 8,
  },
  {
    name: 'games.game_zone',
    pathname: '/game-zone',
    image: <Image src={GameZoneIcon} alt={''} />,
    isActive: true,
    mobileMenuOrder: 6,
  },
]
const mobileGames = [...games].sort((a, b) => (a.mobileMenuOrder || 0) - (b.mobileMenuOrder || 0))

const variants = {
  out: {
    opacity: 0,
    y: 0,
    transition: {
      duration: 0,
    },
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
    },
  },
}
const Transition = ({ children }: any) => {
  const { pathname } = useRouter()
  return (
    <div className="effect-1">
      <AnimatePresence initial={false} mode="wait">
        <motion.div key={pathname} variants={variants} animate="in" initial="out" exit="out">
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
